import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, formatDateTime } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import InvoiceItemsTable from '../components/InvoiceItemsTable';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Download, 
  Printer, 
  Pencil, 
  FileText, 
  CheckCircle, 
  X,
  Info,
  Package,
  Calculator,
  RefreshCw,
  } from 'lucide-react';

const getQuotationStatusColor = (status) => {
    const colors = {
        draft: 'secondary',
        sent: 'info',
        accepted: 'success',
        rejected: 'danger',
        converted: 'primary'
    };
    return colors[status] || 'secondary';
};

const ViewQuotation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    const [quotation, setQuotation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');

    useEffect(() => {
        fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchQuotation = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/quotations/${id}`);
            setQuotation(response.data.quotation);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!newStatus) {
            setError('Please select a status');
            return;
        }

        try {
            await api.put(`/quotations/${id}/status`, { status: newStatus });
            setSuccess('Quotation status updated successfully');
            setShowStatusModal(false);
            fetchQuotation();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update status');
        }
    };

    const handleConvertToInvoice = async () => {
        if (!window.confirm('Are you sure you want to convert this quotation to an invoice?')) {
            return;
        }

        try {
            const response = await api.post(`/quotations/${id}/convert`);
            setSuccess('Quotation converted to invoice successfully! Redirecting...');
            setTimeout(() => {
                navigate(`/invoice/${response.data.invoice._id}`);
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to convert quotation');
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this quotation? This action cannot be undone.')) {
            return;
        }

        try {
            await api.delete(`/quotations/${id}`);
            setSuccess('Quotation deleted successfully! Redirecting...');
            setTimeout(() => {
                navigate('/quotations');
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete quotation');
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const response = await api.get(`/quotations/${id}/download`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Quotation-${quotation.quotationNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError('Failed to download PDF');
        }
    };

    const handlePrintPDF = async () => {
        try {
            const response = await api.get(`/quotations/${id}/download`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.width = '0px';
            iframe.style.height = '0px';
            iframe.style.border = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => {
                    document.body.removeChild(iframe);
                    window.URL.revokeObjectURL(url);
                }, 1000);
            };
        } catch (err) {
            setError('Failed to print PDF');
        }
    };

    if (loading) return <Layout><Loading /></Layout>;
    if (!quotation) return <Layout><Alert type="error" message="Quotation not found" /></Layout>;

    const canEdit = quotation.status !== 'converted';
    const canDelete = quotation.status !== 'converted';
    const canConvert = quotation.status !== 'converted';

    return (
        <div>
            <div>
                <div className="page-header">
                    <h1>Quotation Details</h1>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn btn-secondary" onClick={() => navigate('/quotations')} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ArrowLeft size={18} /> Back
                        </button>
                        <button className="btn btn-primary" onClick={handleDownloadPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Download size={18} /> Download PDF
                        </button>
                        <button className="btn btn-primary" onClick={handlePrintPDF} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Printer size={18} /> Print
                        </button>
                        {canEdit && (
                            <button className="btn btn-primary" onClick={() => navigate(`/quotations/${id}/edit`)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Pencil size={18} /> Edit Quotation
                            </button>
                        )}
                        {canConvert && (
                            <button className="btn btn-primary" onClick={handleConvertToInvoice} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FileText size={18} /> Convert to Invoice
                            </button>
                        )}
                    </div>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}
                {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

                {/* Quotation Header */}
                <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                            <h2>{quotation.quotationNumber}</h2>
                            <p>Date: {formatDate(quotation.quotationDate)}</p>
                            <p>Valid Until: {formatDate(quotation.validUntil)}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div>
                                <span className={`badge badge-${getQuotationStatusColor(quotation.status)}`} style={{ fontSize: '14px', padding: '8px 12px' }}>
                                    {quotation.status.toUpperCase()}
                                </span>
                            </div>
                            {quotation.status !== 'converted' && (
                                <button
                                    className="btn btn-secondary btn-small"
                                    onClick={() => {
                                        setNewStatus(quotation.status);
                                        setShowStatusModal(true);
                                    }}
                                    style={{ marginTop: '10px' }}
                                >
                                    Change Status
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Customer Details */}
                 <div className="card">
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Info size={18} /> Customer Information
                        </div>
                    </div>
                    <div className="form-row">
                        <div>
                            <strong>Name:</strong> {quotation.customerDetails.name}<br />
                            <strong>Phone:</strong> {quotation.customerDetails.phone}<br />
                            {quotation.customerDetails.email && (
                                <><strong>Email:</strong> {quotation.customerDetails.email}<br /></>
                            )}
                        </div>
                        <div>
                            {quotation.customerDetails.address && (
                                <><strong>Address:</strong> {quotation.customerDetails.address}<br /></>
                            )}
                            <strong>City:</strong> {quotation.customerDetails.city}<br />
                            <strong>State:</strong> {quotation.customerDetails.state}<br />
                            {quotation.customerDetails.pincode && (
                                <><strong>PIN:</strong> {quotation.customerDetails.pincode}<br /></>
                            )}
                        </div>
                        <div>
                            {quotation.customerDetails.gstNumber && (
                                <><strong>GST Number:</strong> {quotation.customerDetails.gstNumber}<br /></>
                            )}
                            <strong>Place of Supply:</strong> {quotation.customerDetails.placeOfSupply}<br />
                            <strong>Tax Type:</strong> {quotation.isIGST ? 'IGST' : 'CGST + SGST'}
                        </div>
                    </div>
                </div>

                {/* Items */}
                 <div className="card">
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Package size={18} /> Items
                        </div>
                    </div>
                    <InvoiceItemsTable items={quotation.items} onItemsChange={() => { }} readOnly={true} />
                </div>

                {/* Totals */}
                 <div className="card">
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calculator size={18} /> Quotation Totals
                        </div>
                    </div>
                    <table style={{ width: '100%', maxWidth: '500px', marginLeft: 'auto' }}>
                        <tbody>
                            <tr>
                                <td><strong>Subtotal:</strong></td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.subtotal)}</td>
                            </tr>
                            {quotation.discount > 0 && (
                                <tr style={{ color: '#dc2626' }}>
                                    <td><strong>Discount:</strong></td>
                                    <td style={{ textAlign: 'right' }}>- {formatCurrency(quotation.discount)}</td>
                                </tr>
                            )}
                            {quotation.isIGST ? (
                                <tr>
                                    <td><strong>IGST:</strong></td>
                                    <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.taxBreakup.igst)}</td>
                                </tr>
                            ) : (
                                <>
                                    <tr>
                                        <td><strong>CGST:</strong></td>
                                        <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.taxBreakup.cgst)}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>SGST:</strong></td>
                                        <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.taxBreakup.sgst)}</td>
                                    </tr>
                                </>
                            )}
                            <tr>
                                <td><strong>Total Tax:</strong></td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.taxBreakup.total)}</td>
                            </tr>
                            {quotation.roundOff !== 0 && (
                                <tr>
                                    <td><strong>Round Off:</strong></td>
                                    <td style={{ textAlign: 'right' }}>{formatCurrency(quotation.roundOff)}</td>
                                </tr>
                            )}
                            <tr style={{ borderTop: '2px solid #333', fontSize: '20px' }}>
                                <td><strong>Grand Total:</strong></td>
                                <td style={{ textAlign: 'right' }}><strong>{formatCurrency(quotation.grandTotal)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Admin Actions */}
                {canEdit && (
                    <div className="card no-print">
                        <div className="card-header">Actions</div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {canConvert && (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleConvertToInvoice}
                                >
                                    Convert to Invoice
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                >
                                    Delete Quotation
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Notes and Terms */}
                {(quotation.notes || quotation.termsAndConditions) && (
                    <div className="card">
                        {quotation.notes && (
                            <>
                                <div className="card-header">Notes</div>
                                <p style={{ whiteSpace: 'pre-wrap', marginBottom: '15px' }}>{quotation.notes}</p>
                            </>
                        )}
                        {quotation.termsAndConditions && (
                            <>
                                <div className="card-header">Terms & Conditions</div>
                                <p style={{ whiteSpace: 'pre-wrap' }}>{quotation.termsAndConditions}</p>
                            </>
                        )}

                        {/* Signature Block */}
                        <div className="print-footer" style={{ marginTop: '50px', display: 'flex', justifyContent: 'flex-end' }}>
                            <div className="signature-box" style={{ textAlign: 'center' }}>
                                <div style={{ height: '60px' }}></div>
                                <div style={{ borderTop: '1px solid #000', width: '200px', paddingTop: '5px' }}>
                                    <strong>Authorized Signatory</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Converted Invoice Link */}
                 {quotation.status === 'converted' && quotation.convertedToInvoice && (
                    <div className="card" style={{ borderLeft: '4px solid #06b6d4' }}>
                        <div className="card-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <RefreshCw size={18} /> Conversion Information
                            </div>
                        </div>
                        <p><strong>Converted At:</strong> {formatDateTime(quotation.convertedAt)}</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate(`/invoice/${quotation.convertedToInvoice._id || quotation.convertedToInvoice}`)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'fit-content' }}
                        >
                            <FileText size={18} /> View Created Invoice
                        </button>
                    </div>
                )}


                {/* Status Change Modal */}
                <Modal
                    isOpen={showStatusModal}
                    onClose={() => setShowStatusModal(false)}
                    title="Change Quotation Status"
                     footer={
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowStatusModal(false)}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <X size={18} /> Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleUpdateStatus}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                <CheckCircle size={18} /> Update Status
                            </button>
                        </div>
                    }
                >
                    <div className="form-group">
                        <label className="form-label">Select New Status *</label>
                        <select
                            className="form-control"
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                        >
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default ViewQuotation;
